import React, { useCallback, useEffect, useRef, useState } from "react";
import { multipartUploadViaWS, type UploadStartedInfo } from "../uploader";
import { useStore } from "../store";
import { getWs } from "../wsConnection";
import { useFocusTrap } from "../utils/focusTrap";
import type { AppVersionMetadata } from "../types/appVersionMetadata";
import { BuildArchiveSection } from "./BuildArchiveSection";
import { ZipTreeDropdown } from "./ZipTreeDropdown";
import type { ZipAnalysis } from "../utils/zipAnalyzer";
import { waitForAck } from "../utils/waitForAck";
import { CommandArgsBuilder } from "./CommandArgsBuilder";
import { EnvVarsEditor } from "./EnvVarsEditor";
import { logger } from '../log';
import styles from './AddVersionDialog.module.css';
import { correlationId } from "@interlucent/ids";
import { send } from "../ws";

const log = logger('create-app');

interface CreateApplicationDialogProps {
  projectId: string;
  onClose: () => void;
  onUploadStarted: (info: UploadStartedInfo) => void;
}

export function CreateApplicationDialog({
  projectId,
  onClose,
  onUploadStarted
}: CreateApplicationDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [appName, setAppName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [zipAnalysis, setZipAnalysis] = useState<ZipAnalysis | null>(null);
  const [showExecDropdown, setShowExecDropdown] = useState(false);
  const execFieldRef = useRef<HTMLDivElement>(null);
  const [metadata, setMetadata] = useState<AppVersionMetadata>({
    gameEngine: "unreal",
    operatingSystem: "windows",
    executablePath: ""
  });
  const [fieldsTouched, setFieldsTouched] = useState({
    gameEngine: false,
    operatingSystem: false,
    executablePath: false
  });

  // Enable focus trap
  useFocusTrap(modalRef, true);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleMetadataChange = useCallback((patch: Partial<AppVersionMetadata>) => {
    setMetadata(prev => ({ ...prev, ...patch }));
  }, []);

  const handleArchiveChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    setMetadata(prev => ({ ...prev, executablePath: "" }));
    setFieldsTouched(prev => ({ ...prev, executablePath: false }));
    setZipAnalysis(null);
  }, []);

  const markFieldTouched = useCallback((field: keyof typeof fieldsTouched) => {
    setFieldsTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  useEffect(() => {
    if (!zipAnalysis) {
      setShowExecDropdown(false);
    }
  }, [zipAnalysis]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!execFieldRef.current) return;
      if (!execFieldRef.current.contains(event.target as Node)) {
        setShowExecDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleExecutablePick = useCallback((path: string) => {
    markFieldTouched("executablePath");
    setMetadata(prev => ({ ...prev, executablePath: path }));
    setShowExecDropdown(false);
  }, [markFieldTouched]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = appName.trim();
    setNameError(null);
    if (!trimmedName) {
      setNameError("Application name is required");
      return;
    }
    if (!metadata.executablePath.trim()) {
      alert("Executable path is required");
      return;
    }
    if (!selectedFile) {
      alert("Select a build archive before continuing");
      return;
    }

    let createdAppId: string;
    try {
      createdAppId = await createApplicationOnServer(projectId, trimmedName);
    } catch (error: any) {
      log.error('application creation failed', error);
      setNameError(error?.message || "Unable to create application");
      return;
    }

    onClose();

    let currentUploadId: string | undefined;
    const checkPaused = () => {
      if (!currentUploadId) return false;
      const currentState = useStore.getState();
      return currentState.uploadProgress[currentUploadId]?.paused === true;
    };

    if (!getWs()) {
      log.error('WebSocket not available for upload');
      return;
    }

    try {
      await multipartUploadViaWS(
        selectedFile,
        () => {}, // Progress callback not needed - tracked elsewhere
        projectId,
        (info) => {
          currentUploadId = info.uploadId;
          onUploadStarted(info);
        },
        undefined, // resumeUploadId
        {
          ...metadata,
          executablePath: metadata.executablePath.trim()
        },
        undefined,
        createdAppId,
        undefined, // replaceVersionId
        checkPaused // Check if paused
      );
    } catch (e: any) {
      if (e.message === 'upload_paused') {
        log.info('upload paused by user');
      } else {
        log.error('upload error', e);
      }
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-app-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="create-app-title" className={styles.modalTitle}>
          Create Application
        </h2>

        <form onSubmit={handleFormSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="app-name-input" className={styles.label}>
                Application Name *
              </label>
              <input
                id="app-name-input"
                type="text"
                value={appName}
                onChange={(e) => {
                  setAppName(e.target.value);
                  if (nameError) setNameError(null);
                }}
                placeholder="e.g., My Game Server"
                autoFocus
                className={styles.input}
                aria-invalid={nameError ? true : undefined}
                aria-describedby={nameError ? "app-name-error" : undefined}
                required
              />
              {nameError && (
                <div
                  id="app-name-error"
                  role="alert"
                  style={{ color: 'var(--gray-10)', marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}
                >
                  {nameError}
                </div>
              )}
            </div>

            <BuildArchiveSection
              dialogId="create-application"
              selectedFile={selectedFile}
              onFileChange={handleArchiveChange}
              metadata={metadata}
              onMetadataChange={handleMetadataChange}
              canAutofill={{
                gameEngine: !fieldsTouched.gameEngine,
                operatingSystem: !fieldsTouched.operatingSystem,
                executablePath: !fieldsTouched.executablePath
              }}
              onAnalysisChange={setZipAnalysis}
            />

            <div className={styles.formGroup}>
              <label htmlFor="game-engine-select" className={styles.label}>
                Game Engine *
              </label>
              <select
                id="game-engine-select"
                value={metadata.gameEngine}
                onChange={(e) => {
                  markFieldTouched("gameEngine");
                  setMetadata(prev => ({ ...prev, gameEngine: e.target.value as "unreal" | "unity" }));
                }}
                className={styles.select}
                required
              >
                <option value="unreal">Unreal Engine</option>
                <option value="unity">Unity</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="os-select" className={styles.label}>
                Operating System *
              </label>
              <select
                id="os-select"
                value={metadata.operatingSystem}
                onChange={(e) => {
                  markFieldTouched("operatingSystem");
                  setMetadata(prev => ({ ...prev, operatingSystem: e.target.value as "windows" | "linux" }));
                }}
                className={styles.select}
                required
              >
                <option value="windows">Windows</option>
              </select>
            </div>

            <div className={`${styles.formGroup} ${styles.execFieldGroup}`} ref={execFieldRef}>
              <label htmlFor="exec-path-input" className={styles.label}>
                Executable Path *
              </label>
              <div className={styles.execInputWrapper}>
                <input
                  id="exec-path-input"
                  type="text"
                  value={metadata.executablePath}
                  onFocus={() => zipAnalysis && setShowExecDropdown(true)}
                  onChange={(e) => {
                    markFieldTouched("executablePath");
                    setMetadata(prev => ({ ...prev, executablePath: e.target.value }));
                  }}
                  placeholder="e.g., GameServer.exe or bin/server"
                  className={styles.input}
                  required
                />
                <ZipTreeDropdown
                  analysis={zipAnalysis}
                  isOpen={Boolean(showExecDropdown && zipAnalysis && selectedFile)}
                  selectedPath={metadata.executablePath}
                  onSelect={handleExecutablePick}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="cmd-args-input" className={styles.label}>
                Command Arguments (optional)
              </label>
              <CommandArgsBuilder
                id="cmd-args-input"
                value={metadata.cmdArgs || ''}
                onChange={(value) => setMetadata(prev => ({ ...prev, cmdArgs: value }))}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="env-vars-textarea" className={styles.label}>
                Environment Variables (optional)
              </label>
              <EnvVarsEditor
                id="env-vars-textarea"
                value={metadata.environmentVariables || ''}
                onChange={(value) => setMetadata(prev => ({ ...prev, environmentVariables: value }))}
              />
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.button}
                disabled={!selectedFile}
              >
                Upload Build
              </button>
            </div>
        </form>
      </div>
    </div>
  );
}

function slugifyName(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

async function createApplicationOnServer(
  projectId: string,
  name: string
) {
  const corr = correlationId();
  send('application.create', { projectId, name, slugHint: slugifyName(name) }, corr);
  try {
    const payload = await waitForAck<{ id: string }>(corr);
    if (!payload?.id) {
      throw new Error("Application creation failed");
    }
    return payload.id;
  } catch (error: any) {
    throw error;
  }
}
